var __decorate = this && this.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
    r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
    d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/* eslint-disable max-lines */
import { action, computed, extendObservable, flow, observable } from 'mobx';
import { Store } from 'bernie-plugin-mobx';
import { getErrorString, NOOP_LOGGER } from 'bernie-logger';
import { timeoutPromise } from 'bernie-core';
import { adaptPropertySearch, getPropertySearchInputWithSearchId } from 'stores/adapters';
import { evaluateAndLogExperiment } from 'stores/utils';
import { isVRBO } from 'stores/context-store-utils';
import { mapOmnitureData } from 'stores/adapters/bex-api/search/search-results-adapter';
import { SearchCriteriaModel } from 'stores/models/search-criteria-model';
import { STORE_EVENTS } from 'stores/events/store-events';
import { isTablet } from 'utils/is-mobile';
export class SearchResultsStore extends Store {
  // public static INITIAL_LISTING_FETCH_COUNT = 5;
  // public static SECONDARY_LISTING_FETCH_COUNT = 95;
  static getInitialListingFetchCount(context, experiments) {
    // Control:   load 5 results for mobile, load 5 results for desktop, load 5 results for tablet
    // Variant 1: Load 3 results for mobile, Load 3 results for desktop, Load 4 results for tablet
    // Variant 2: Load 4 results for mobile, Load 4 results for desktop, Load 5 results for tablet
    const exposureBucket = evaluateAndLogExperiment('SRP_Server_Side_Initial_Count', context, experiments);
    if (exposureBucket === 1) {
      if (isTablet(context)) {
        return 4;
      }
      // mobile or desktop
      return 3;
    } else if (exposureBucket === 2) {
      if (isTablet(context)) {
        return 5;
      }
      // mobile or desktop
      return 4;
    }
    return 5;
  }
  static getSecondaryListingFetchCount(context, experiments) {
    return 100 - SearchResultsStore.getInitialListingFetchCount(context, experiments);
  }
  constructor(bexApiSource, state, logger = NOOP_LOGGER, l10n) {
    super(state, logger);
    this.bexApiSource = bexApiSource;
    this.maxHotelsDisplayedPerLoad = SearchResultsStore.HOTEL_TOTAL_RENDER_COUNT;
    this.prefetchedMultiItemSession = false;
    this.initialLoadSearchPagination = undefined;
    /*
     * The flow wrapper is an optional alternative to async / await that makes it easier
     * to work with MobX actions and allows the promise returned to be cancelled.
     * https://mobx.js.org/actions.html#using-flow-instead-of-async--await-
     */
    //
    this.createSearchResultsFetchFlow = flow(function* (analyticsStore, context, timeout, additionalCriteria = {}, multiItem, analyticsSearch, experiments, includeDynamicMap) {
      var _a, _b;
      // @ts-expect-error self alias required for mobx flow syntax and typescript compatibility
      // eslint-disable-next-line @typescript-eslint/no-this-alias,@typescript-eslint/ban-ts-comment,consistent-this
      const self = this;
      self.prefetchedMultiItemSession = false;
      self.shouldFetch = false;
      self.isLoading = true;
      self.error = undefined;
      self.errors = [];
      self.propertySearchListings = [];
      self.omnitureData = [];
      const multiItemContext = multiItem ? multiItem.multiItemContext : null;
      const criteriaJson = (_a = self.searchCriteria) === null || _a === void 0 ? void 0 : _a.toJSON();
      const bexApiCriteria = Object.assign(Object.assign({}, criteriaJson), additionalCriteria);
      const propertySearchFetch = timeoutPromise(self.searchProperties(context, multiItemContext, bexApiCriteria, analyticsSearch, self.initialLoadSearchPagination, includeDynamicMap), timeout, 'propertySearchFetch timeout error');
      try {
        const [bexApiResults] = yield Promise.all([propertySearchFetch]);
        if (multiItem && bexApiResults) {
          const shoppingContextMultiItemId = self.getMultiItemId(bexApiResults.data);
          multiItem.updateSessionId(shoppingContextMultiItemId);
          self.shoppingContextMultiItemId = shoppingContextMultiItemId;
        }
        self.errors = bexApiResults === null || bexApiResults === void 0 ? void 0 : bexApiResults.errors;
        const data = adaptPropertySearch(bexApiResults);
        self.pendingSearchResultsFetchFlow = null;
        if (data) {
          self.setOverFilteredModel(data.overFilteredModel);
          const searchCriteria = (_b = self.searchCriteria) === null || _b === void 0 ? void 0 : _b.clone();
          const defaultRequestFields = data.defaultRequestFields;
          if (searchCriteria && defaultRequestFields) {
            Object.assign(searchCriteria, defaultRequestFields);
          }
          self.searchCriteria = searchCriteria;
          Object.assign(self, data);
          if (self.numberOfListingsFetched() > 0 && !self.isFetchSecondaryListingBatch(context, experiments)) {
            analyticsStore === null || analyticsStore === void 0 ? void 0 : analyticsStore.publishClientSidePayload(self.omnitureData);
            self.omnitureData = [];
          }
        }
        self.isLoading = false;
        return data;
      } catch (err) {
        self.logErrorEvent(STORE_EVENTS.FETCH_ERROR, err);
        // don't assign the error to this.error, as errors in this catch are technical and NOT user-friendly
        self.error = true;
        self.isLoading = false;
        self.shouldFetch = true;
        // recover and pass object with error details
        return {
          error: err
        };
      }
    }.bind(this));
    this.searchCriteria = new SearchCriteriaModel({}, l10n);
    this.fetch = this.fetch.bind(this);
    this.searchProperties = this.searchProperties.bind(this);
  }
  hydrate(data) {
    var _a;
    delete data.source;
    delete data.bexApiSource;
    (_a = this.searchCriteria) === null || _a === void 0 ? void 0 : _a.hydrate(data.searchCriteria || {});
    delete data.searchCriteria;
    extendObservable(this, data);
  }
  get isError() {
    var _a;
    return Boolean(!this.isLoading && (this.error || ((_a = this.errors) === null || _a === void 0 ? void 0 : _a.length) || this.overFiltered || this.errorResponse || this.dialogErrorResponse));
  }
  get hasAtLeastOneProperty() {
    return Boolean(this.propertySearchListings && this.propertySearchListings.length > 0);
  }
  showNextPage(analyticsStore, context, multiItem, uiPage, analyticsSearch, experiments) {
    var _a, _b, _c, _d;
    if (!((_b = (_a = this.pagination) === null || _a === void 0 ? void 0 : _a.subSets) === null || _b === void 0 ? void 0 : _b.nextSubSet)) return;
    const {
      startingIndex
    } = ((_d = (_c = this.pagination) === null || _c === void 0 ? void 0 : _c.subSets) === null || _d === void 0 ? void 0 : _d.nextSubSet) || {};
    const pagInput = {
      startingIndex,
      size: SearchResultsStore.PROPERTIES_FETCH_COUNT
    };
    this.showItemsByPage(analyticsStore, context, multiItem, uiPage, pagInput, analyticsSearch, experiments);
  }
  showPreviousPage(analyticsStore, context, multiItem, uiPage, analyticsSearch, experiments) {
    var _a, _b, _c, _d;
    if (!((_b = (_a = this.pagination) === null || _a === void 0 ? void 0 : _a.subSets) === null || _b === void 0 ? void 0 : _b.previousSubset)) return;
    const {
      startingIndex
    } = ((_d = (_c = this.pagination) === null || _c === void 0 ? void 0 : _c.subSets) === null || _d === void 0 ? void 0 : _d.previousSubset) || {};
    const pagInput = {
      startingIndex,
      size: SearchResultsStore.PROPERTIES_FETCH_COUNT
    };
    this.showItemsByPage(analyticsStore, context, multiItem, uiPage, pagInput, analyticsSearch, experiments);
  }
  showItemsByPage(analyticsStore, context, multiItem, uiPage, paginationInput, analyticsSearch, experiments, showProgressIndicator = true) {
    var _a, _b, _c;
    const initialSearchId = analyticsSearch.searchId;
    const multiItemContext = (_a = multiItem === null || multiItem === void 0 ? void 0 : multiItem.multiItemContext) !== null && _a !== void 0 ? _a : null;
    const includeDynamicMap = isVRBO(context);
    const propertySearchFetch = this.searchProperties(context, multiItemContext, this.searchCriteria, analyticsSearch, paginationInput, includeDynamicMap);
    this.isPaginationLoading = true;
    if (showProgressIndicator) {
      this.showProgressIndicator(uiPage);
    }
    if (((_c = (_b = this.pagination) === null || _b === void 0 ? void 0 : _b.trigger) === null || _c === void 0 ? void 0 : _c.__typename) === 'LodgingPaginationNavigationButtons') {
      this.propertySearchListings = [];
    }
    propertySearchFetch.then(({
      data,
      errors,
      extensions
    }) => {
      var _a, _b, _c, _d, _e, _f;
      // TODO: this might be required to be replicated in shared-ui after property-listing query migration -- ideally aborting the query
      const currentSearchId = analyticsSearch.searchId;
      if (initialSearchId !== currentSearchId) {
        this.logger.logEvent(STORE_EVENTS.BEX_API_SEARCH_RESULTS_STORE_EXPIRED_PAGINATION_FETCH_ERROR, 'expired pagination query data');
        return;
      }
      // -----------------------------------------------------------------------------------
      this.errors = errors !== null && errors !== void 0 ? errors : [];
      (_a = this.omnitureData) === null || _a === void 0 ? void 0 : _a.push(mapOmnitureData(extensions.analytics));
      if (data === null || data === void 0 ? void 0 : data.propertySearch.pagination) {
        this.pagination = data === null || data === void 0 ? void 0 : data.propertySearch.pagination;
      }
      if (multiItem) {
        multiItem.updateSessionId(this.getMultiItemId(data));
      }
      if (data === null || data === void 0 ? void 0 : data.propertySearch.dynamicMap) {
        this.dynamicMap = data === null || data === void 0 ? void 0 : data.propertySearch.dynamicMap;
      }
      if (data === null || data === void 0 ? void 0 : data.propertySearch.summary.resultMessages) {
        this.resultMessages = data === null || data === void 0 ? void 0 : data.propertySearch.summary.resultMessages;
      }
      const isShoppingPwaSearchQuery = (multiItem === null || multiItem === void 0 ? void 0 : multiItem.isPackageShoppingPath()) || evaluateAndLogExperiment('EG_Property_Search_Query_Migration', context, experiments, this.pageData) === 0;
      if (isShoppingPwaSearchQuery && this.omnitureData) {
        analyticsStore === null || analyticsStore === void 0 ? void 0 : analyticsStore.publishClientSidePayload(this.omnitureData);
        this.omnitureData = [];
      }
      if (((_c = (_b = data === null || data === void 0 ? void 0 : data.propertySearch.pagination) === null || _b === void 0 ? void 0 : _b.trigger) === null || _c === void 0 ? void 0 : _c.__typename) === 'LodgingPaginationNavigationButtons') {
        this.propertySearchListings = (_d = data === null || data === void 0 ? void 0 : data.propertySearch.propertySearchListings) !== null && _d !== void 0 ? _d : [];
        return;
      }
      this.propertySearchListings = [...((_e = this.propertySearchListings) !== null && _e !== void 0 ? _e : []), ...((_f = data === null || data === void 0 ? void 0 : data.propertySearch.propertySearchListings) !== null && _f !== void 0 ? _f : [])];
    }).catch(error => {
      this.logger.logEvent(STORE_EVENTS.BEX_API_SEARCH_RESULTS_STORE_FETCH_ERROR, getErrorString(error));
    }).finally(() => {
      // isPaginationLoading needs to change with new omniture data in one step
      this.isPaginationLoading = false;
      if (showProgressIndicator) {
        this.hideProgressIndicator(uiPage);
      }
    });
  }
  numberOfListingsFetched() {
    var _a, _b;
    const lodgingCardCount = (_a = this.propertySearchListings) === null || _a === void 0 ? void 0 : _a.filter(listing => listing.__typename === 'LodgingCard').length;
    if (lodgingCardCount && lodgingCardCount > 0) {
      return lodgingCardCount;
    }
    return ((_b = this.propertySearchListings) === null || _b === void 0 ? void 0 : _b.length) || 0;
  }
  get thereAreMoreListingsToFetch() {
    var _a, _b, _c;
    return this.numberOfListingsFetched() < ((_a = this.totalPropertyCount) !== null && _a !== void 0 ? _a : 0) && Boolean((_c = (_b = this.pagination) === null || _b === void 0 ? void 0 : _b.subSets) === null || _c === void 0 ? void 0 : _c.nextSubSet);
  }
  isFetchSecondaryListingBatch(context, experiments) {
    if (this.numberOfListingsFetched() === 0) return false;
    const initialFetchCount = SearchResultsStore.getInitialListingFetchCount(context, experiments);
    return this.thereAreMoreListingsToFetch && this.numberOfListingsFetched() <= initialFetchCount;
  }
  hideProgressIndicator(uiPage) {
    uiPage === null || uiPage === void 0 ? void 0 : uiPage.hideProgressIndicator();
  }
  showProgressIndicator(uiPage) {
    uiPage === null || uiPage === void 0 ? void 0 : uiPage.showProgressIndicator();
  }
  updateSearchCriteria(searchCriteria) {
    this.searchCriteria = searchCriteria;
    this.shouldFetch = true;
  }
  updateIsLoading(isLoading) {
    this.isLoading = isLoading;
  }
  createSessionBeforeMultiItemFetch(multiItem, context, incomingSearchCriteria) {
    this.isLoading = true;
    this.shouldFetch = false;
    this.propertySearchListings = [];
    this.prefetchedMultiItemSession = true;
    return multiItem.createMultiItemSession(context, incomingSearchCriteria).then(misId => incomingSearchCriteria.misId = misId).catch(() => {
      this.isLoading = false;
      this.shouldFetch = true;
      this.prefetchedMultiItemSession = false;
      this.error = true;
    });
  }
  fetch(analyticsStore, context, timeout, additionalCriteria, multiItem, analyticsSearch, experiments, includeDynamicMap) {
    var _a;
    if (!this.shouldFetch && !this.prefetchedMultiItemSession) {
      return Promise.resolve(this);
    }
    // cancel previous search fetch promise to prevent a race condition
    (_a = this.pendingSearchResultsFetchFlow) === null || _a === void 0 ? void 0 : _a.cancel();
    this.pendingSearchResultsFetchFlow = this.createSearchResultsFetchFlow(analyticsStore, context, timeout, additionalCriteria, multiItem, analyticsSearch, experiments, includeDynamicMap);
    return this.pendingSearchResultsFetchFlow;
  }
  searchProperties(context, multiItemContext, combinedCriteria, analyticsSearch, searchPagination, includeDynamicMap) {
    var _a;
    const variables = getPropertySearchInputWithSearchId(context, analyticsSearch, combinedCriteria, multiItemContext, searchPagination, includeDynamicMap);
    return this.bexApiSource.searchProperties(variables, (_a = this.pageId) !== null && _a !== void 0 ? _a : '');
  }
  logErrorEvent(event, err) {
    if (this.logger) {
      this.logger.logEvent(event, getErrorString(err));
    }
  }
  // Your editor may be telling you that this is unused. It is lying to you. Don't trust it.
  // In createSearchResultsFetchFlow we create a reference to `this` and call `self.setOverFilteredModel`.
  setOverFilteredModel(overFilteredModel) {
    if (this.overFilteredModel && !overFilteredModel) {
      this.overFilteredModel = {};
    }
  }
  clearDefaultRequestFields() {
    this.defaultRequestFields = {};
  }
  clearPagination() {
    // todo: simplify this to `this.pagination = null` after removing the nonnullable from generated types
    Object.assign(this, {
      pagination: null
    });
  }
  setHotelsMaxDisplayToTotalRenderCount() {
    this.hotelsMaxDisplay = SearchResultsStore.HOTEL_TOTAL_RENDER_COUNT;
  }
  setExpandForm(shouldExpandForm) {
    this.expandForm = shouldExpandForm;
  }
  getPageLoadNumber() {
    var _a;
    return ((_a = this.hotelsMaxDisplay) !== null && _a !== void 0 ? _a : 0) / this.maxHotelsDisplayedPerLoad;
  }
  getMultiItemId(data) {
    var _a, _b, _c;
    return (_c = (_b = (_a = data === null || data === void 0 ? void 0 : data.propertySearch) === null || _a === void 0 ? void 0 : _a.shoppingContext) === null || _b === void 0 ? void 0 : _b.multiItem) === null || _c === void 0 ? void 0 : _c.id;
  }
}
SearchResultsStore.INITIAL_HOTEL_RENDER_COUNT = 50;
SearchResultsStore.PROPERTIES_FETCH_COUNT = 50;
SearchResultsStore.HOTEL_TOTAL_RENDER_COUNT = 50;
SearchResultsStore.DEFAULTS = {
  expandForm: false,
  foregoClientFetch: false,
  propertyListingAdaptexAnalyticsSuccessEvents: [],
  propertySearchListings: [],
  hotelsMaxDisplay: SearchResultsStore.INITIAL_HOTEL_RENDER_COUNT,
  isLoading: true,
  lodgingPriceBreakDown: {},
  loyaltyMessagingModel: undefined,
  messagingModule: undefined,
  messages: [],
  omnitureData: [],
  overFilteredModel: undefined,
  pageData: {},
  shouldFetch: true,
  totalPropertyCount: 0,
  isPaginationLoading: false
};
__decorate([observable], SearchResultsStore.prototype, "error", void 0);
__decorate([observable], SearchResultsStore.prototype, "errors", void 0);
__decorate([observable], SearchResultsStore.prototype, "expandForm", void 0);
__decorate([observable], SearchResultsStore.prototype, "foregoClientFetch", void 0);
__decorate([observable], SearchResultsStore.prototype, "propertySearchListings", void 0);
__decorate([observable], SearchResultsStore.prototype, "propertyListingAdaptexAnalyticsSuccessEvents", void 0);
__decorate([observable], SearchResultsStore.prototype, "hotelsMaxDisplay", void 0);
__decorate([observable], SearchResultsStore.prototype, "isLoading", void 0);
__decorate([observable], SearchResultsStore.prototype, "loyaltyMessagingModel", void 0);
__decorate([observable], SearchResultsStore.prototype, "omnitureData", void 0);
__decorate([observable], SearchResultsStore.prototype, "overFiltered", void 0);
__decorate([observable], SearchResultsStore.prototype, "overFilteredModel", void 0);
__decorate([observable], SearchResultsStore.prototype, "shouldFetch", void 0);
__decorate([observable], SearchResultsStore.prototype, "resultMessages", void 0);
__decorate([observable], SearchResultsStore.prototype, "messagingModule", void 0);
__decorate([observable], SearchResultsStore.prototype, "totalPropertyCount", void 0);
__decorate([observable.ref], SearchResultsStore.prototype, "pageData", void 0);
__decorate([observable.ref], SearchResultsStore.prototype, "searchCriteria", void 0);
__decorate([observable], SearchResultsStore.prototype, "shoppingContextMultiItemId", void 0);
__decorate([observable], SearchResultsStore.prototype, "errorResponse", void 0);
__decorate([observable], SearchResultsStore.prototype, "dialogErrorResponse", void 0);
__decorate([observable], SearchResultsStore.prototype, "pagination", void 0);
__decorate([observable], SearchResultsStore.prototype, "mapTrigger", void 0);
__decorate([observable], SearchResultsStore.prototype, "isPaginationLoading", void 0);
__decorate([observable], SearchResultsStore.prototype, "dynamicMap", void 0);
__decorate([computed], SearchResultsStore.prototype, "isError", null);
__decorate([computed], SearchResultsStore.prototype, "hasAtLeastOneProperty", null);
__decorate([action], SearchResultsStore.prototype, "showNextPage", null);
__decorate([action], SearchResultsStore.prototype, "showPreviousPage", null);
__decorate([computed], SearchResultsStore.prototype, "thereAreMoreListingsToFetch", null);
__decorate([action], SearchResultsStore.prototype, "updateSearchCriteria", null);
__decorate([action], SearchResultsStore.prototype, "updateIsLoading", null);
__decorate([action], SearchResultsStore.prototype, "createSessionBeforeMultiItemFetch", null);
__decorate([action], SearchResultsStore.prototype, "fetch", null);
__decorate([action], SearchResultsStore.prototype, "createSearchResultsFetchFlow", void 0);
__decorate([action], SearchResultsStore.prototype, "clearDefaultRequestFields", null);
__decorate([action], SearchResultsStore.prototype, "clearPagination", null);
__decorate([action], SearchResultsStore.prototype, "setHotelsMaxDisplayToTotalRenderCount", null);
__decorate([action], SearchResultsStore.prototype, "setExpandForm", null);